import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Field,
  reduxForm,
  formValueSelector,
  SubmissionError,
  submit,
  change,
} from 'redux-form';
import {
  Modal,
  Grid,
  Label,
  TextArea,
  Form,
  FormField,
  Card,
  Button,
  List,
} from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import isInt from 'validator/lib/isInt';
import {
  setCouponAssignMerchantsModalShown,
  setCouponBusinessIdValidationResult,
} from 'state/coupons/actions';
import {
  COUPON_ASSIGNED_MERCHANTS_FORM,
  COUPON_FORM,
} from 'constants/billingCoupon/formConstants';
import { COUPON_ASSIGN_MERCHANTS_BUSINESS_ID_REQUIRED } from 'constants/billingCoupon/validationConstants';

function parseBusinessIds(businessIds) {
  if (!businessIds) {
    return [];
  }
  return businessIds.split('\n');
}

function formatBusinessIds(businessIds) {
  if (!businessIds) {
    return '';
  }

  return businessIds.join('\n');
}

const getMerchantsAssigned = businessIds => {
  return [
    ...new Set((businessIds || []).filter(b => isInt(b.trim(), { min: 0 }))),
  ].map(n => parseInt(n, 10));
};

const countMerchantsAssigned = businessIds =>
  getMerchantsAssigned(businessIds).length;

// eslint-disable-next-line react/prop-types
function customTextArea({ input, meta: { touched, error }, ...rest }) {
  const errorContent = touched && !!error && error;
  return (
    <FormField error={errorContent}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <TextArea {...input} {...rest} />
      {errorContent && <Label prompt content={errorContent} pointing="above" />}
    </FormField>
  );
}

function CouponAssignMerchants({
  open,
  onClose,
  handleSubmit,
  businessIdValidationResult,
  submitting,
}) {
  const dispatch = useDispatch();
  const formSelector = formValueSelector(COUPON_ASSIGNED_MERCHANTS_FORM);
  const businessIdsInForm = useSelector(state =>
    formSelector(state, 'businessIds')
  );
  const merchantsAssignedCount = useMemo(
    () => countMerchantsAssigned(businessIdsInForm),
    [businessIdsInForm]
  );

  const invalidBusinessIds = useMemo(() => {
    if (!businessIdValidationResult) {
      return [];
    }
    return businessIdValidationResult.invalid_business_ids;
  }, [businessIdValidationResult]);

  const onSubmitButtonClicked = useCallback(
    () => dispatch(submit(COUPON_ASSIGNED_MERCHANTS_FORM)),
    [dispatch]
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>Assign Merchant</Modal.Header>
      <Modal.Content>
        <Form onSubmit={handleSubmit}>
          <Grid columns={2}>
            <Grid.Column width={8}>
              <strong>Business ID</strong>
              <br />
              <p>
                You can copy and paste one column from spreadsheet to this field
              </p>
              <Field
                id="business-ids"
                name="businessIds"
                component={customTextArea}
                style={{ minHeight: '500px' }}
                parse={parseBusinessIds}
                format={formatBusinessIds}
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <strong>Information</strong>
              <br />
              <p>Updates of assigning merchant is showed here</p>
              <Card fluid>
                <Card.Content>
                  Merchant assigned
                  <span style={{ float: 'right' }}>
                    {merchantsAssignedCount}
                  </span>
                </Card.Content>
                <Card.Content style={{ minHeight: '450px', height: '450px' }}>
                  {invalidBusinessIds.length > 0 && (
                    <>
                      <span style={{ color: 'red' }}>
                        Invalid Business ID
                        <span style={{ float: 'right' }}>
                          {invalidBusinessIds.length}
                        </span>
                      </span>
                      <p>These IDs will be removed once you click assign</p>
                      <List
                        items={invalidBusinessIds}
                        style={{ overflowY: 'auto', height: '360px' }}
                      />
                    </>
                  )}
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button basic onClick={onClose} disabled={submitting}>
          Back
        </Button>
        <Button positive onClick={onSubmitButtonClicked} disabled={submitting}>
          Assign
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

CouponAssignMerchants.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  businessIdValidationResult: PropTypes.shape({
    input_count: PropTypes.number.isRequired,
    valid_count: PropTypes.number.isRequired,
    invalid_count: PropTypes.number.isRequired,
    invalid_business_ids: PropTypes.arrayOf(PropTypes.number).isRequired,
  }),
  submitting: PropTypes.bool.isRequired,
};

CouponAssignMerchants.defaultProps = {
  businessIdValidationResult: null,
};

export default reduxForm({
  form: COUPON_ASSIGNED_MERCHANTS_FORM,
  onSubmit: ({ businessIds }, dispatch, { businessIdValidationResult }) => {
    const merchantsAssigned = new Set(getMerchantsAssigned(businessIds));
    if (businessIdValidationResult) {
      businessIdValidationResult.invalid_business_ids.forEach(bId =>
        merchantsAssigned.delete(bId)
      );
    }

    if (merchantsAssigned.size === 0) {
      return Promise.reject(
        new SubmissionError({
          businessIds: COUPON_ASSIGN_MERCHANTS_BUSINESS_ID_REQUIRED,
        })
      );
    }
    dispatch(change(COUPON_FORM, 'businessIds', [...merchantsAssigned]));
    dispatch(setCouponAssignMerchantsModalShown(false));
    dispatch(setCouponBusinessIdValidationResult(null));
    return Promise.resolve();
  },
})(CouponAssignMerchants);
